/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxfrag */
import { lazy, Suspense, useCallback, useEffect, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import PropTypes from 'prop-types';

import { type Journey, useAppProvisioning } from '@atlassian/app-provisioning';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { type SurfaceModule } from '@atlassian/surface-sdk';

import { type Actions } from '../../../index';
import { AppRecommendation } from '../app-recommendation';
import { type AppRecommendationContent } from '../app-recommendation/types';
import { type AppRecommendationsProps } from '../types';

import {
	getExperimentCohort,
	NUMBER_OF_RECOMMENDED_APPS,
	TBLZ_SWITCHER_ROLE_BASED_EXPERIMENT_NAME,
} from './experiment';
import { type AppRecommendationsSectionListProps } from './types';

const UndoDismissFlag = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_app-recommendation-flag" */ '../app-recommendation-flag'
		),
);

const ListResetStyles = css({
	padding: 0,
	margin: 0,
});

const containerStyles = css({
	listStyleType: 'none',
	margin: 0,
});

export const AppRecommendationsList: SurfaceModule<
	AppRecommendationsSectionListProps,
	AppRecommendationsProps,
	Actions
> = ({
	actionBus,
	flagNotInterestedSubtitle,
	flagNotInterestedTitle,
	apps,
	mutate,
	context,
	recommendationReasonsModalAcceptButton,
	recommendationReasonsModalExplanation,
	recommendationReasonsModalPrivacyPolicyLinkHref,
	recommendationReasonsModalPrivacyPolicyLinkText,
	recommendationReasonsModalTitle,
	adControlsMenuLabel,
	adControlsMenuNotInterested,
	adControlsMenuWhyAmISeeingThis,
}) => {
	const appProvisioning = useAppProvisioning();
	const { fireViewedUIEvent } = useFireCrossFlowAnalyticsEvents();

	// TBLZ-494: Switcher - Updated Recommendations and Targeting
	// go/trailblazerexperiments
	const tblzSwitcherRoleBasedExperimentCohort = getExperimentCohort(context);

	if (
		context.locale?.startsWith('en') &&
		tblzSwitcherRoleBasedExperimentCohort !== 'not-enrolled' &&
		apps.length > 0
	) {
		fireViewedUIEvent(
			{
				// Using @ts-ignore here to avoid the strict typing in `surface-analytics`.
				// `surface-analytics` doesn't have track events, so using a UI event to send exposure event.
				// @ts-ignore
				actionSubject: 'feature',
				// @ts-ignore
				action: 'exposed',
				// @ts-ignore
				actionSubjectId: TBLZ_SWITCHER_ROLE_BASED_EXPERIMENT_NAME,
			},
			{
				source: 'atlassianSwitcherDiscoverProductsSection',
				recommendedProductIds: apps.map((app) => app.productId),
			},
			{
				flagKey: TBLZ_SWITCHER_ROLE_BASED_EXPERIMENT_NAME,
				cohort: tblzSwitcherRoleBasedExperimentCohort,
			},
		);
	}

	if (
		tblzSwitcherRoleBasedExperimentCohort === 'experiment' &&
		apps.length > NUMBER_OF_RECOMMENDED_APPS
	) {
		apps.splice(NUMBER_OF_RECOMMENDED_APPS);
	}
	// TBLZ-494

	// stores the list of dismissed apps
	const [dismissedApps, setDismissedApps] = useState<AppRecommendationContent[]>([]);

	// stores the order/arrangement of all apps
	const [_, setStateRegister] = useState<AppRecommendationContent[][]>([apps]);

	const removeFlag = (id: string | number) => {
		setDismissedApps((current) => current.filter((flag) => flag.productId !== id));
	};

	useEffect(() => {
		const unsubscribe = actionBus?.on('DISMISS_APP', ({ productId }) => {
			let app = apps.find((searchApp) => searchApp.productId === productId);
			if (app) {
				setDismissedApps([app, ...dismissedApps]);
				setStateRegister((listOfPrevStates) => {
					return [...listOfPrevStates, apps];
				});
			}
		});
		return () => {
			if (unsubscribe) {
				unsubscribe();
			}
		};
	}, [apps, actionBus, dismissedApps]);

	const dispatchSurfaceViewedEvent = useCallback(() => {
		actionBus?.dispatch('USER_VIEW_DISCOVERY_SURFACE', {
			action: {
				action: 'viewed',
				actionSubject: 'section',
				actionSubjectId: 'atlassianSwitcherDiscoverProducts',
			},
			source: 'atlassianSwitcher',
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		dispatchSurfaceViewedEvent();
	}, [dispatchSurfaceViewedEvent]);

	useEffect(() => {
		if (apps.length === 0) {
			actionBus?.dispatch('USE_EMPTY_APPS_TITLE');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [apps.length]);

	const appRecommendationMessages = {
		recommendationReasonsModalAcceptButton,
		recommendationReasonsModalExplanation,
		recommendationReasonsModalPrivacyPolicyLinkHref,
		recommendationReasonsModalPrivacyPolicyLinkText,
		recommendationReasonsModalTitle,
	};

	return (
		<ul css={ListResetStyles} data-testid={'discovery-sdk-component--app-recommendations-list'}>
			{apps.map((app, index) => (
				<li css={containerStyles} key={index}>
					<AppRecommendation
						{...app}
						{...appRecommendationMessages}
						actionBus={actionBus}
						context={context}
						mutate={mutate}
						journey={app.journey}
						appProvisioning={appProvisioning}
						key={(app.productId, index)}
						adControlsMenuLabel={adControlsMenuLabel}
						adControlsMenuNotInterested={adControlsMenuNotInterested}
						adControlsMenuWhyAmISeeingThis={adControlsMenuWhyAmISeeingThis}
					/>
				</li>
			))}
			<Suspense fallback={null}>
				<UndoDismissFlag
					title={flagNotInterestedTitle}
					description={flagNotInterestedSubtitle}
					flags={dismissedApps.map((app) => ({
						...app,
						...appRecommendationMessages,
					}))}
					removeFlag={removeFlag}
					closeDismissedFlag={(productId) => {
						actionBus?.dispatch('CLOSE_APP_DISMISSED_FLAG', {
							productId,
							action: {
								action: 'clicked',
								actionSubject: 'button',
								actionSubjectId: 'adControlsFlagClose',
							},
							source: 'atlassianSwitcherDiscoverProductsSection',
						});
					}}
				/>
			</Suspense>
		</ul>
	);
};

AppRecommendationsList.propTypes = {
	apps: PropTypes.arrayOf(
		PropTypes.shape({
			productId: PropTypes.string.isRequired,
			name: PropTypes.string.isRequired,
			description: PropTypes.string.isRequired,
			logo: PropTypes.string.isRequired,
			journey: PropTypes.oneOf(['get-started', 'decide', 'discover'] as Journey[]).isRequired,
			reasons: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
		}).isRequired,
	).isRequired,
	recommendationReasonsModalExplanation: PropTypes.string.isRequired,
	recommendationReasonsModalPrivacyPolicyLinkText: PropTypes.string.isRequired,
	recommendationReasonsModalPrivacyPolicyLinkHref: PropTypes.string.isRequired,
	recommendationReasonsModalAcceptButton: PropTypes.string.isRequired,
	adControlsMenuLabel: PropTypes.string.isRequired,
	adControlsMenuNotInterested: PropTypes.string.isRequired,
	adControlsMenuWhyAmISeeingThis: PropTypes.string.isRequired,
	flagNotInterestedTitle: PropTypes.string.isRequired,
	flagNotInterestedSubtitle: PropTypes.string.isRequired,
};
